import React, { memo } from "react";
import { Link as ChakraLink, type LinkProps } from "@chakra-ui/react";

export type PublicLinkVariant = "inline" | "nav" | "footer" | "action";

export type PublicLinkProps = Omit<LinkProps, "variant" | "children"> & {
  children: React.ReactNode;
  variant?: PublicLinkVariant;
};

const PUBLIC_LINK_FOCUS_PROPS = {
  outlineWidth: "2",
  outlineStyle: "solid",
  outlineColor: "moveon.primary",
  outlineOffset: "2px",
} as const;

const PUBLIC_LINK_VARIANTS = {
  inline: {
    color: "moveon.primary",
    fontWeight: "semibold",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    _hover: {
      color: "moveon.primary",
      textDecorationColor: "moveon.primary",
    },
  },
  nav: {
    color: "moveon.publicFont",
    fontWeight: "medium",
    textDecoration: "none",
    _hover: {
      color: "moveon.primary",
      textDecoration: "none",
    },
  },
  footer: {
    color: "moveon.publicFont",
    fontWeight: "medium",
    textDecoration: "none",
    _hover: {
      color: "moveon.primary",
      textDecoration: "underline",
      textUnderlineOffset: "3px",
    },
  },
  action: {
    color: "moveon.primary",
    fontWeight: "bold",
    textDecoration: "none",
    _hover: {
      color: "moveon.primary",
      textDecoration: "underline",
      textUnderlineOffset: "3px",
    },
  },
} as const;

function PublicLink({
  children,
  variant = "inline",
  ...linkProps
}: PublicLinkProps) {
  return (
    <ChakraLink
      rounded="sm"
      transitionDuration="fast"
      transitionProperty="color, background-color, text-decoration-color"
      _focusVisible={PUBLIC_LINK_FOCUS_PROPS}
      {...PUBLIC_LINK_VARIANTS[variant]}
      {...linkProps}
    >
      {children}
    </ChakraLink>
  );
}

export default memo(PublicLink);
